import React from 'react';
import { Section, Container, Card } from '../common';
import { ArrowRight, BarChart3, MessageCircle, PackageCheck } from 'lucide-react';

const posts = [
  {
    icon: <BarChart3 size={28} />,
    category: 'Gestão',
    title: 'Como reduzir a dependência dos marketplaces no delivery',
    excerpt: 'Estratégias para fortalecer o canal próprio, proteger margem e criar relacionamento direto com seus clientes.',
    readTime: '6 min',
    slug: 'reduzir-dependencia-marketplaces',
    intro: 'Marketplaces podem gerar demanda, mas também aumentam custos e afastam o restaurante do relacionamento direto com o cliente.',
    highlights: [
      'Crie um cardápio próprio com identidade da loja.',
      'Use WhatsApp para relacionamento e recompra.',
      'Acompanhe pedidos, clientes e canais para decidir onde investir.'
    ],
    sections: [
      {
        title: 'Comece pelo canal próprio',
        body: 'O cardápio digital próprio permite que o cliente compre direto da marca do restaurante. Isso fortalece reconhecimento, reduz atrito e cria uma base de clientes que pode ser trabalhada em campanhas futuras.'
      },
      {
        title: 'Use dados para decidir',
        body: 'Acompanhe origem dos pedidos, ticket médio, clientes recorrentes e produtos mais vendidos. Com esses indicadores, o lojista entende quando vale investir em campanha própria e quando o marketplace deve ser usado apenas como canal complementar.'
      },
      {
        title: 'Transforme atendimento em recompra',
        body: 'WhatsApp, cupons e recuperação de vendas ajudam o restaurante a manter contato com clientes que já demonstraram interesse, sem depender sempre de novas taxas e comissões.'
      }
    ]
  },
  {
    icon: <PackageCheck size={28} />,
    category: 'Operação',
    title: 'Ficha técnica: o caminho para saber quanto custa cada produto',
    excerpt: 'Entenda como receita, insumos e CMV ajudam o restaurante a vender com mais previsibilidade.',
    readTime: '5 min',
    slug: 'ficha-tecnica-custo-produto',
    intro: 'A ficha técnica conecta produto, ingredientes, quantidade e custo, criando uma base confiável para calcular margem e CMV.',
    highlights: [
      'Liste os ingredientes de cada produto.',
      'Atualize custos de insumos com frequência.',
      'Use o CMV para revisar preço, promoções e margem.'
    ],
    sections: [
      {
        title: 'Padronize a receita',
        body: 'Cada produto deve ter ingredientes, unidade de medida e quantidade. Essa padronização reduz variação na produção e ajuda a treinar equipe.'
      },
      {
        title: 'Calcule custo automaticamente',
        body: 'Quando o custo dos insumos está atualizado, o sistema pode recalcular o custo do produto e mostrar se o preço de venda ainda faz sentido.'
      },
      {
        title: 'Conecte com estoque',
        body: 'Ao relacionar ficha técnica e estoque, o restaurante consegue prever rupturas, reduzir desperdício e pausar produtos quando um ingrediente essencial acabar.'
      }
    ]
  },
  {
    icon: <MessageCircle size={28} />,
    category: 'Vendas',
    title: 'WhatsApp no delivery: atendimento rápido sem perder controle',
    excerpt: 'Veja como automação, cardápio digital e atendimento humano podem trabalhar no mesmo fluxo.',
    readTime: '4 min',
    slug: 'whatsapp-delivery-atendimento',
    intro: 'O WhatsApp é um canal forte de relacionamento, mas precisa de fluxo organizado para não virar gargalo operacional.',
    highlights: [
      'Responda rápido com saudação e link do cardápio.',
      'Transfira para humano quando o cliente pedir ajuda.',
      'Registre conversas para melhorar atendimento e vendas.'
    ],
    sections: [
      {
        title: 'Automatize o primeiro contato',
        body: 'Uma saudação automática com link do cardápio reduz espera e direciona o cliente para um fluxo de pedido mais organizado.'
      },
      {
        title: 'Tenha atendimento humano quando necessário',
        body: 'Quando o cliente pede suporte, o bot deve pausar e a conversa precisa ir para uma fila de atendimento no POS ou portal.'
      },
      {
        title: 'Meça o que acontece no canal',
        body: 'Tempo de resposta, conversas transferidas e pedidos originados do WhatsApp ajudam o lojista a entender a eficiência do atendimento.'
      }
    ]
  }
];

export const blogPosts = posts;

export const getBlogPostBySlug = (slug) => posts.find((post) => post.slug === slug);

export const BlogList = ({ compact = false }) => {
  return (
    <Section id="blog" className="bg-white blog-list-section">
      <Container>
        <div className="row align-items-end mb-5 g-4">
          <div className="col-lg-8" data-aos="fade-up">
            <span className="section-eyebrow">Conteúdo para donos de restaurante</span>
            <h2 className="fw-bold mb-3">Ideias práticas para vender mais e operar melhor</h2>
            <p className="text-secondary lead mb-0">
              Um espaço para falar de gestão, delivery próprio, estoque, atendimento,
              fidelização e crescimento sustentável para operações de alimentação.
            </p>
          </div>
          <div className="col-lg-4 text-lg-end" data-aos="fade-up">
            <a href={compact ? '/#contato' : '#contato'} className="btn btn-outline-custom">Receber diagnóstico</a>
          </div>
        </div>

        <div className="row g-4">
          {posts.map((post, index) => (
            <div key={post.slug} className="col-md-6 col-lg-4" data-aos="fade-up" data-aos-delay={index * 100}>
              <Card className="blog-card h-100">
                <div className="blog-card__meta">
                  <span>{post.category}</span>
                  <span>{post.readTime}</span>
                </div>
                <div className="blog-card__icon" aria-hidden="true">
                  {post.icon}
                </div>
                <h3 className="h5 fw-bold mb-3">{post.title}</h3>
                <p className="text-secondary small mb-4">{post.excerpt}</p>
                <a className="blog-card__link" href={`/blog/${post.slug}`} aria-label={`Ler artigo: ${post.title}`}>
                  Ler conteúdo
                  <ArrowRight size={16} />
                </a>
              </Card>
            </div>
          ))}
        </div>
      </Container>
    </Section>
  );
};

export default BlogList;
